import React from 'react';
import { Card, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import { t } from '../../../utils/textUtils';
import { formatPercentage, formatDate, computeTotalProgressPercent } from '../utils/formatters';

const ProgressBangunanTable = ({ 
  isProjectMode, 
  isUnitMode, 
  dataByUnit, 
  selectedBangunan,
  onDownloadExcel,
  onDownloadPDF
}) => {
  return (
    <Card 
      sx={{ 
        mb: 3, 
        border: '1px solid #E2E8F0', 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden'
      }}
    >
      {/* Card Header */}
      <Box sx={{
        px: 3, py: 2,
        borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#FAFBFC'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            background: 'linear-gradient(135deg, #0284C7, #0EA5E9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(2,132,199,0.3)'
          }}>
            <TableChartIcon sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>
              {t('PROGRESS BANGUNAN')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>Detail progress per bangunan/unit</Typography>
          </Box>
        </Box>
        {isProjectMode && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              sx={{
                borderColor: '#22C55E',
                color: '#22C55E',
                '&:hover': {
                  borderColor: '#16A34A',
                  bgcolor: '#F0FDF4'
                }
              }}
              startIcon={<DownloadIcon />}
              onClick={onDownloadExcel}
            >
              Excel
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#EF4444',
                  color: '#EF4444',
                  '&:hover': {
                    borderColor: '#DC2626',
                    bgcolor: '#FEF2F2'
                  }
                }}
                startIcon={<DownloadIcon />}
                onClick={onDownloadPDF}
              >
                PDF
              </Button>
            </Box>
          )}
        </Box>
      <Box sx={{ p: 3 }}>
        {isProjectMode ? (
          dataByUnit && dataByUnit.length > 0 ? (
            <TableContainer sx={{ border: '1px solid #E2E8F0', borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('BANGUNAN')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('MULAI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('SELESAI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('DURASI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('PROGRES SEBELUMNYA')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('PROGRES MINGGU INI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('PROGRESS TOTAL')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataByUnit.map((unit, index) => {
                    const progressBefore = parseFloat(unit.progressBeforeThisWeekPercentage) || 0;
                    const progressThisWeek = parseFloat(unit.progressThisWeekPercentage) || 0;
                    const progressTotal = computeTotalProgressPercent(unit);

                    return (
                      <TableRow 
                        key={`${unit.moduleId}-${index}`}
                        sx={{ 
                          '&:hover': { bgcolor: '#F8FAFC' },
                          '&:last-child td': { border: 0 }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                            🏢 {unit.moduleName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="#64748B">
                            {formatDate(unit.startDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="#64748B">
                            {formatDate(unit.endDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="#64748B">
                            {unit.durationDays ? `${unit.durationDays} HARI` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            label={formatPercentage(progressBefore)}
                            size="small"
                            sx={{
                              bgcolor: '#F1F5F9',
                              color: '#64748B',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            label={formatPercentage(progressThisWeek)}
                            size="small"
                            sx={{
                              bgcolor: '#DBEAFE',
                              color: '#1D4ED8',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            label={formatPercentage(progressTotal)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: progressTotal >= 80 ? '#DCFCE7' : progressTotal >= 50 ? '#FEF3C7' : '#FEE2E2',
                              color: progressTotal >= 80 ? '#166534' : progressTotal >= 50 ? '#92400E' : '#991B1B'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              bgcolor: '#F8FAFC',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="#64748B" sx={{ fontWeight: 500 }}>
                {t('TIDAK ADA BANGUNAN DALAM PROJECT INI')}
              </Typography>
            </Box>
          )
        ) : isUnitMode ? (
          dataByUnit && dataByUnit.length > 0 ? (
            <TableContainer sx={{ border: '1px solid #E2E8F0', borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{t('BANGUNAN')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('MULAI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('SELESAI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('DURASI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('PROGRES SEBELUMNYA')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('PROGRES MINGGU INI')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">{t('PROGRESS TOTAL')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataByUnit.filter(unit => unit.moduleId === parseInt(selectedBangunan)).map((unit, index) => {
                    const progressBefore = parseFloat(unit.progressBeforeThisWeekPercentage) || 0;
                    const progressThisWeek = parseFloat(unit.progressThisWeekPercentage) || 0;
                    const progressTotal = computeTotalProgressPercent(unit);

                    return (
                      <TableRow 
                        key={`${unit.moduleId}-${index}`}
                        sx={{ 
                          '&:hover': { bgcolor: '#F8FAFC' },
                          '&:last-child td': { border: 0 }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                            🏢 {unit.moduleName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="#64748B">
                            {formatDate(unit.startDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="#64748B">
                            {formatDate(unit.endDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" color="#64748B">
                            {unit.durationDays ? `${unit.durationDays} HARI` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            label={formatPercentage(progressBefore)}
                            size="small"
                            sx={{
                              bgcolor: '#F1F5F9',
                              color: '#64748B',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            label={formatPercentage(progressThisWeek)}
                            size="small"
                            sx={{
                              bgcolor: '#DBEAFE',
                              color: '#1D4ED8',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            label={formatPercentage(progressTotal)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: progressTotal >= 80 ? '#DCFCE7' : progressTotal >= 50 ? '#FEF3C7' : '#FEE2E2',
                              color: progressTotal >= 80 ? '#166534' : progressTotal >= 50 ? '#92400E' : '#991B1B'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              bgcolor: '#F8FAFC',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="#64748B" sx={{ fontWeight: 500 }}>
                {t('TIDAK ADA DATA BANGUNAN')}
              </Typography>
            </Box>
          )
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: '#F8FAFC',
            borderRadius: 1
          }}>
            <Typography variant="body2" color="#64748B" sx={{ fontWeight: 500 }}>
              {t('TIDAK ADA PROGRESS YANG DITEMUKAN')}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default ProgressBangunanTable;
